import { HttpClient, HttpParams } from '@angular/common/http';
import { EventEmitter, Injectable } from '@angular/core';
import { StoredAssets } from '@app/features/app-settings/states/assets.state';
import { environment } from '@env/environment';
import { Store } from '@ngxs/store';
import { Observable } from 'rxjs';
import { AppConfigApi, PodcastFeedData } from '../models/app-config.model';
import { AppConfigimages, UserRadio } from '../states/app-configuration.state';
import { MonetizationSettings } from '../states/monetizations.state';
import { PrivacySettings } from '../states/privacy.state';

const APP_CONFIGURATION_URLS = {
  getNameAndLogo: (appId: number) =>
    `${environment.urls.MOBILE_API}/app/${appId}/get-name-and-logo`,
  postNameAndLogo: (appId?: number) =>
    appId
      ? `${environment.urls.MOBILE_API}/app/${appId}/create`
      : `${environment.urls.MOBILE_API}/app/create`,
  getAppConfig: (appId: number) =>
    `${environment.urls.MOBILE_API}/app/${appId}/simulator-configuration`,
  postAppConfig: (appId: number) =>
    `${environment.urls.MOBILE_API}/app/${appId}/save-simulator-configuration`,
  postAssetImage: (appId: number) =>
    `${environment.urls.MOBILE_API}/app/${appId}/upload-image`,
  checkAppName: () => `${environment.urls.MOBILE_API}/app/name-available`,
  plan: (appId: number, planId: number) =>
    `${environment.urls.MOBILE_API}/app/${appId}/plan?plan=${planId}`,
  getUserRadios: (userId: number) =>
    `${environment.urls.MOBILE_API}/app/get-radios?id=${userId}`,
  getIcecastMountPoints: (url: string) =>
    `${environment.urls.MOBILE_API}/app/get-mountpoints?url=${url}`,
  finishCreation: (appId: number) =>
    `${environment.urls.MOBILE_API}/app/${appId}/update-assets`,
  updateAssets: (appId: number) =>
    `${environment.urls.MOBILE_API}/app/${appId}/update-assets`,
  getPodcastFeedData: (appId: number, feedUrl: string) =>
    `${environment.urls.MOBILE_API}/app/${appId}/check-podcast-rss/${feedUrl}`,
};

@Injectable({
  providedIn: 'root',
})
export class AppConfigurationService {
  onOpenMenu = new EventEmitter<boolean>();

  constructor(
    private readonly http: HttpClient,
    private readonly store: Store,
  ) {}

  getNameAndLogo(appId: number): Observable<Partial<AppConfigApi>> {
    return this.http.get<Partial<AppConfigApi>>(
      APP_CONFIGURATION_URLS.getNameAndLogo(appId),
    );
  }

  postNameAndLogo(name: string, logo?: Blob, appId?: number): Observable<AppConfigApi> {
    const formData = new FormData();
    formData.append('name', name);
    if (logo) {
      formData.append('logo', logo);
    }

    return this.http.post<any>(APP_CONFIGURATION_URLS.postNameAndLogo(appId), formData);
  }

  getAppConfig(appId: number): Observable<AppConfigApi> {
    return this.http.get<AppConfigApi>(APP_CONFIGURATION_URLS.getAppConfig(appId));
  }

  postAppConfig(
    config: AppConfigApi,
    appId: number,
    configImages?: AppConfigimages,
  ): Observable<AppConfigApi> {
    const formData = new FormData();
    formData.append('data', JSON.stringify(config));

    if (configImages) {
      Object.keys(configImages).forEach(key => {
        const file = configImages[key];
        formData.append(key, file);
      });
    }

    return this.http.post<AppConfigApi>(
      APP_CONFIGURATION_URLS.postAppConfig(appId),
      formData,
    );
  }

  plan(planId: number, appId: number): Observable<boolean> {
    return this.http.put<boolean>(APP_CONFIGURATION_URLS.plan(appId, planId), undefined);
  }

  finishCreation(
    appId: number,
    monetizationSettings?: MonetizationSettings,
  ): Observable<AppConfigApi> {
    const formData = new FormData();
    if (monetizationSettings) {
      Object.keys(monetizationSettings).forEach(key => {
        const value = monetizationSettings[key];
        formData.append(key, value);
      });
    }

    return this.http.post<AppConfigApi>(
      APP_CONFIGURATION_URLS.finishCreation(appId),
      formData,
    );
  }

  updateAssets(
    appId: number,
    monetizationSettings?: MonetizationSettings,
    newName?: string,
    privacySettings?: PrivacySettings,
  ): Observable<AppConfigApi> {
    const formData = new FormData();

    if (monetizationSettings) {
      Object.keys(monetizationSettings).forEach(key => {
        const value = monetizationSettings[key];
        formData.append(key, value);
      });
    }

    if (privacySettings) {
      Object.keys(privacySettings).forEach(key => {
        const value = privacySettings[key];
        if (value !== null && value !== undefined) {
          // convert boolean to 0/1 for cmpOption
          const formValue = typeof value === 'boolean' ? (value ? '1' : '0') : value;
          formData.append(key, formValue);
        }
      });
    }

    if (newName) {
      formData.append('name', newName);
    }

    return this.http.post<AppConfigApi>(
      APP_CONFIGURATION_URLS.updateAssets(appId),
      formData,
    );
  }

  postAssetImage(appId: number, appAssets: StoredAssets): Observable<AppConfigApi> {
    const formData = new FormData();
    const assets = appAssets || {};
    Object.keys(assets).forEach(key => {
      const file = assets[key].file;
      formData.append(key, file);
    });

    return this.http.post<AppConfigApi>(
      APP_CONFIGURATION_URLS.postAssetImage(appId),
      formData,
    );
  }

  checkAppName(name: string): Observable<boolean> {
    const params = new HttpParams().set('name', name);

    return this.http.get<boolean>(APP_CONFIGURATION_URLS.checkAppName(), { params });
  }

  getUserRadios(userId: number): Observable<UserRadio[]> {
    return this.http.get<UserRadio[]>(APP_CONFIGURATION_URLS.getUserRadios(userId));
  }

  getIcecastMountPoints(url: string): Observable<string[]> {
    return this.http.get<string[]>(APP_CONFIGURATION_URLS.getIcecastMountPoints(url));
  }

  getPodcastFeedData(appId: number, feedUrl: string): Observable<PodcastFeedData> {
    const encodedUrl = btoa(feedUrl);

    return this.http.get<PodcastFeedData>(
      APP_CONFIGURATION_URLS.getPodcastFeedData(appId, encodedUrl),
    );
  }

  openMenu() {
    this.onOpenMenu.emit();
  }
}
